import { useMemo } from "react";
import type { ModeloFormulario, NivelDesempenho, QuestaoFormulario } from "../api";
import { IconStar } from "../icons";

/**
 * O FORMULÁRIO DE AVALIAÇÃO DE DESEMPENHO DA SERCAMP (FO 094), na tela.
 *
 * É o mesmo papel: as cinco QUESTÕES GERAIS, as QUESTÕES ESPECÍFICAS do cargo,
 * nota de 1 a 10 em cada uma (com a escala à vista) e, no fim, pontos fortes,
 * pontos de melhoria e observações.
 *
 * Serve nas duas pontas — a página pública do gestor e a tela do RH — por isso
 * não fala com a API: recebe o modelo e devolve as respostas.
 */

export interface RespostasFormulario {
  notas: Record<string, string>;
  pontos_fortes: string;
  pontos_melhoria: string;
  observacoes: string;
}

export const RESPOSTAS_VAZIAS: RespostasFormulario = {
  notas: {},
  pontos_fortes: "",
  pontos_melhoria: "",
  observacoes: "",
};

/** Média das notas preenchidas — o "TOTAL DE PONTOS" do formulário. */
export function mediaDasNotas(notas: Record<string, string>): number | null {
  const valores = Object.values(notas ?? {})
    .map((v) => (String(v).trim() === "" ? NaN : Number(v)))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (valores.length === 0) return null;
  return Math.round((valores.reduce((a, b) => a + b, 0) / valores.length) * 10) / 10;
}

export function nivelDe(nota: number | null, niveis: NivelDesempenho[]): string {
  if (nota == null) return "—";
  return niveis.find((n) => nota >= n.de && nota <= n.ate)?.nivel ?? "—";
}

/** A escala do formulário (RUIM · REGULAR · BOM · EXCELENTE). */
export function EscalaNiveis({ niveis }: { niveis: NivelDesempenho[] }) {
  return (
    <div className="fo-escala">
      {niveis.map((n) => (
        <div key={n.nivel} className="fo-nivel">
          <strong>{n.nivel}</strong>
          <span className="fo-faixa">{n.faixa}</span>
          <small>{n.descricao}</small>
        </div>
      ))}
    </div>
  );
}

export default function FormularioAvaliacao({
  modelo,
  niveis,
  respostas,
  onChange,
}: {
  modelo: ModeloFormulario | null;
  niveis: NivelDesempenho[];
  respostas: RespostasFormulario;
  onChange: (r: RespostasFormulario) => void;
}) {
  const { gerais, especificas } = useMemo(() => {
    const q = modelo?.questoes ?? [];
    return {
      gerais: q.filter((x) => x.tipo === "geral"),
      especificas: q.filter((x) => x.tipo === "especifica"),
    };
  }, [modelo]);

  const media = mediaDasNotas(respostas.notas);
  const respondidas = Object.values(respostas.notas).filter((v) => String(v).trim() !== "").length;
  const total = (modelo?.questoes ?? []).length;

  function setNota(chave: string, valor: string) {
    onChange({ ...respostas, notas: { ...respostas.notas, [chave]: valor } });
  }

  if (!modelo) {
    return (
      <p className="fo-sem-modelo">
        Escolha o formulário desta função para ver as perguntas.
      </p>
    );
  }

  return (
    <div className="fo">
      <div className="fo-resumo">
        <span className="fo-total">
          <IconStar size={14} /> {media != null ? media.toFixed(1) : "—"}
          <small>{nivelDe(media, niveis)}</small>
        </span>
        <span className="muted">
          {respondidas} de {total} questões respondidas ({gerais.length} gerais +{" "}
          {especificas.length} da função)
        </span>
      </div>

      {/* Todas as questões ficam à vista: é o formulário do papel, e esconder
          as específicas atrás de um clique fazia parecer que o formulário
          tinha só as cinco perguntas gerais. */}
      <Bloco titulo="Questões gerais" questoes={gerais} respostas={respostas} setNota={setNota} />
      <Bloco
        titulo={`Questões específicas — ${modelo.nome}`}
        questoes={especificas}
        respostas={respostas}
        setNota={setNota}
      />

      <div className="fo-textos">
        <label className="field">
          <span>Pontos fortes</span>
          <textarea
            rows={2}
            value={respostas.pontos_fortes}
            onChange={(e) => onChange({ ...respostas, pontos_fortes: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Pontos de melhorias</span>
          <textarea
            rows={2}
            value={respostas.pontos_melhoria}
            onChange={(e) => onChange({ ...respostas, pontos_melhoria: e.target.value })}
          />
        </label>
        <label className="field field-wide">
          <span>Observações</span>
          <textarea
            rows={2}
            value={respostas.observacoes}
            onChange={(e) => onChange({ ...respostas, observacoes: e.target.value })}
          />
        </label>
      </div>
    </div>
  );
}

function Bloco({
  titulo,
  questoes,
  respostas,
  setNota,
}: {
  titulo: string;
  questoes: QuestaoFormulario[];
  respostas: RespostasFormulario;
  setNota: (chave: string, valor: string) => void;
}) {
  if (questoes.length === 0) return null;

  const corpo = (
    <div className="fo-questoes">
      {questoes.map((q, i) => (
        <div className="fo-questao" key={q.chave}>
          <span className="fo-num">{i + 1}</span>
          <div className="fo-texto">
            <strong>{q.titulo}</strong>
            {q.descricao && <small>{q.descricao}</small>}
          </div>
          <input
            className="fo-nota"
            type="number"
            min={1}
            max={10}
            step="1"
            inputMode="numeric"
            placeholder="—"
            value={respostas.notas[q.chave] ?? ""}
            onChange={(e) => setNota(q.chave, e.target.value)}
          />
        </div>
      ))}
    </div>
  );

  return (
    <section className="fo-bloco">
      <h5>
        {titulo} <span className="badge">{questoes.length}</span>
      </h5>
      {corpo}
    </section>
  );
}
